import { forwardRef, useImperativeHandle } from 'react';
import { FormProvider } from 'react-hook-form';

import { ButtonType } from '@xemplo/button';
import { DawnAddPlus, DawnTag } from '@xemplo/icons';
import { Prompt, PromptDialogSlot, usePromptDialog } from '@xemplo/prompt-dialog';
import { Colour } from '@xemplo/style-constants';
import { maxTagsValidator, TagFieldRhf } from '@xemplo/tag-field';

import * as S from './edit-tags-prompt.styles';
import { PromptDialogRef } from './edit-tags-prompt.types';
import { useTagsField } from './use-tags-field';

export const EditTagsPrompt = forwardRef<PromptDialogRef, { payscheduleId?: string }>(
  ({ payscheduleId }, ref) => {
    const promptDialog = usePromptDialog({
      id: 'edit-tags-prompt',
      extended: true,
    });

    useImperativeHandle(ref, () => promptDialog);

    const {
      methods,
      tagsList,
      handleOnTagCreate,
      handleConfirm,
      isUpdating,
      onOpenChange,
      debouncedSearch,
    } = useTagsField({
      promptDialog,
      payscheduleId,
    });

    return (
      <S.CustomPrompt {...promptDialog.dialogProps}>
        {promptDialog.isOpen ? (
          <FormProvider {...methods}>
            <Prompt {...promptDialog.promptProps}>
              <Prompt.Slot
                name={PromptDialogSlot.CloseButton}
                render={
                  <Prompt.CloseButton
                    id="prompt-close-button"
                    ariaLabel="Close prompt button"
                    onClick={promptDialog.closeDialog}
                  />
                }
              />
              <Prompt.Slot
                name={PromptDialogSlot.Header}
                render={
                  <Prompt.Header
                    extended={promptDialog.promptProps.extended}
                    title="Edit Tags"
                    icon={<DawnTag color={Colour.Blue[500]} />}
                  />
                }
              />
              <Prompt.Slot
                name={PromptDialogSlot.Body}
                render={
                  <Prompt.Body extended={promptDialog.promptProps.extended}>
                    <S.PromptBodyContent>
                      <span>Manage tags linked to this Pay Schedule</span>
                      <span>
                        Use letters, numbers, dashes, or underscores (max 50 characters)
                      </span>
                      <TagFieldRhf
                        className="tag-field"
                        name="tags"
                        aria-label="Select tags from the list"
                        items={tagsList}
                        placeholder="Add tag"
                        leadingIcon={<DawnAddPlus />}
                        onSearchChange={debouncedSearch}
                        onTagCreate={handleOnTagCreate}
                        onOpenChange={onOpenChange}
                        maxTagChar={50}
                        rules={{
                          validate: {
                            maxTags: maxTagsValidator({ maxTags: 10 }),
                          },
                        }}
                      />
                    </S.PromptBodyContent>
                  </Prompt.Body>
                }
              />
              <Prompt.Slot
                name={PromptDialogSlot.Footer}
                render={
                  <Prompt.Footer
                    extended={promptDialog.promptProps.extended}
                    btnPrimary={{
                      label: 'Save',
                      ariaLabel: 'Save tags',
                      type: ButtonType.Primary,
                      onClickAsync: handleConfirm,
                      isLoading: isUpdating,
                    }}
                    btnTertiary={{
                      label: 'Cancel',
                      ariaLabel: 'Cancel',
                      type: ButtonType.Tertiary,
                      onClick: promptDialog.closeDialog,
                    }}
                  />
                }
              />
            </Prompt>
          </FormProvider>
        ) : null}
      </S.CustomPrompt>
    );
  },
);
